import { Formik } from 'formik';
import capitalize from 'lodash/capitalize';
import snakeCase from 'lodash/snakeCase';
import React, { memo, useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

import { CUSTOM_SPINNER_STYLES, PrimaryButton } from '@components/Button/variants';
import CheckBoxField from '@components/Form/CheckBoxField';
import TextField from '@components/Form/TextField';
import NUXForm from '@components/NUXForm';
import SubIndustryList from '@components/OnboardingModal/ProfileFields/SubIndustryList';
import SubJobFunctionList from '@components/OnboardingModal/ProfileFields/SubJobFunctionList';
import CustomSpinner, { CustomSpinnerColor } from '@components/Spinner/CustomSpinner';
import {
  useClearSearchParams,
  useEventTracking,
  useLandingPageLeadCreate,
  useServerSideRendering,
} from '@hooks';
import { RECAPTCHA_SITE_KEY } from '@src/env';
import { TrackingLocation } from '@tracking';
import { ClickTarget, click } from '@tracking';
import {
  COMPANY_SIZE_OPTIONS,
  JOB_FUNCTION_OPTIONS,
  JOB_LEVEL_OPTIONS,
  ORGANIZATION_INDUSTRY_OPTIONS,
  PROFILE_FIELD_OTHER_VALUE,
  PROFILE_OTHER_OPTION,
} from '@utils/backend-api/profileSettings';
import { BLANK_OPTION, BLANK_RECORD_OPTION } from '@utils/constants';
import { AuthoredContentReactToHtmlParser } from '@utils/reactHtmlParserTransform';

import {
  INITIAL_STATE,
  OPTIONAL_FIRST_PARTY_DATA,
  potentialFirstPartyDataFields,
  schema,
} from './constants';
import styles from './formStyles.module.scss';
import { LandingPageFormProps } from './interfaces';

const Form = ({
  advertisementSponsorName,
  advertisementSponsorLogoUrl,
  advertisementClickthroughUrl,
  confirmationText,
  formButtonText,
  formFields,
  formHeadline,
  formVerificationText,
  id,
  leadType,
  newsletterId,
  prefilledFormFields,
  updatedFirstPartyData,
}: LandingPageFormProps): JSX.Element => {
  const [initialValues, setInitialValues] = useState(INITIAL_STATE);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const { mutateAsync: createLandingPageLead } = useLandingPageLeadCreate();
  const reCaptchaRef = useRef<ReCAPTCHA>();
  const hydrated = useServerSideRendering();
  const { trackEvent } = useEventTracking({
    location: TrackingLocation.LandingPage,
  });
  useClearSearchParams(potentialFirstPartyDataFields);

  useEffect(() => {
    const getSelectedOptions = (possibleOptions, profileKey) => {
      const selectedStandardValue = prefilledFormFields?.[profileKey];
      const standardOption =
        possibleOptions?.find((item) => item.value === selectedStandardValue) || BLANK_OPTION;
      const customOption = BLANK_RECORD_OPTION;

      if (selectedStandardValue && !standardOption.label) {
        return {
          standardOption: PROFILE_OTHER_OPTION,
          customOption: { label: capitalize(selectedStandardValue), value: selectedStandardValue },
        };
      }

      return { standardOption, customOption };
    };

    const email = prefilledFormFields?.email;
    const name = prefilledFormFields?.name?.split(' ');
    const companySize = COMPANY_SIZE_OPTIONS.find(
      (item) => item.value === prefilledFormFields?.companySize
    ) || { ...BLANK_OPTION };

    const industryValueAndCustomValue = getSelectedOptions(
      ORGANIZATION_INDUSTRY_OPTIONS,
      'organizationIndustry'
    );
    const jobFunctionValueAndCustomValue = getSelectedOptions(JOB_FUNCTION_OPTIONS, 'jobFunction');
    const jobLevelValueAndCustomValue = getSelectedOptions(JOB_LEVEL_OPTIONS, 'jobLevel');
    const jobFunction = JOB_FUNCTION_OPTIONS.find(
      (item) => item.value === prefilledFormFields?.jobFunction
    );
    const industry = ORGANIZATION_INDUSTRY_OPTIONS.find(
      (item) => item.value === prefilledFormFields?.organizationIndustry
    );
    const subJobFunctionValueAndCustomValue = getSelectedOptions(
      SubJobFunctionList[jobFunction?.label],
      'subJobFunction'
    );
    const subIndustryValueAndCustomValue = getSelectedOptions(
      SubIndustryList[industry?.label],
      'organizationSubIndustry'
    );

    setInitialValues({
      company: prefilledFormFields.companyName
        ? {
            label: capitalize(prefilledFormFields.companyName),
            value: 0,
          }
        : BLANK_RECORD_OPTION,
      companySize: companySize,
      customIndustry: industryValueAndCustomValue.customOption,
      customJobFunction: jobFunctionValueAndCustomValue.customOption,
      customJobLevel: jobLevelValueAndCustomValue.customOption,
      customSubIndustry: subIndustryValueAndCustomValue.customOption,
      customSubJobFunction: subJobFunctionValueAndCustomValue.customOption,
      email: email || '',
      firstName: name?.[0] || '',
      formVerification: true,
      industry: industryValueAndCustomValue.standardOption,
      jobFunction: jobFunctionValueAndCustomValue.standardOption,
      jobLevel: jobLevelValueAndCustomValue.standardOption,
      jobTitle: prefilledFormFields.titleAtCompany
        ? { label: prefilledFormFields.titleAtCompany || '', value: 0 }
        : BLANK_RECORD_OPTION,
      lastName: name?.[1] || '',
      postalCode: prefilledFormFields.postalCode || '',
      subIndustry: subIndustryValueAndCustomValue.standardOption,
      subJobFunction: subJobFunctionValueAndCustomValue.standardOption,
    });
  }, [formFields, prefilledFormFields]);

  const handleSubmit = async (values) => {
    try {
      const refToken = await reCaptchaRef.current.executeAsync();

      const valueMap = {
        company_name: values?.company?.label || '',
        company_size: values?.companySize.value,
        job_function:
          values?.jobFunction.value === PROFILE_FIELD_OTHER_VALUE
            ? values?.customJobFunction.label
            : values?.jobFunction.value,
        job_level:
          values?.jobLevel.value === PROFILE_FIELD_OTHER_VALUE
            ? values?.customJobLevel.label
            : values?.jobLevel.value,
        name: `${values?.firstName} ${values?.lastName}` || '',
        organization_industry:
          values?.industry.value === PROFILE_FIELD_OTHER_VALUE
            ? values?.customIndustry.label
            : values?.industry.value,
        organization_sub_industry:
          values?.subIndustry.value === PROFILE_FIELD_OTHER_VALUE
            ? values?.customSubIndustry.label
            : values?.subIndustry.value,
        postal_code: values?.postalCode,
        sub_job_function:
          values?.subJobFunction.value === PROFILE_FIELD_OTHER_VALUE
            ? values?.customSubJobFunction.label
            : values?.subJobFunction.value,
        title_at_company: values?.jobTitle?.label || '',
      };

      const requiredFields = formFields.reduce((formSchema, formField) => {
        const fieldName = snakeCase(formField);
        formSchema[fieldName] = valueMap[fieldName];
        return formSchema;
      }, {});

      const sanitizedParams = {
        ...requiredFields,
        email: values.email,
        landing_page_id: id,
        lead_type: leadType,
        newsletter_id: newsletterId,
        recaptcha_token: refToken,
      };

      const resp = await createLandingPageLead(sanitizedParams);
      if (resp.success) {
        setShowSuccessMessage((prevState) => !prevState);
      }
      reCaptchaRef.current.reset();
    } catch (e) {
      console.error('Landing Page Error: ', e);
    } finally {
      reCaptchaRef.current.reset();
    }
  };

  return (
    <div className={styles.landingPageForm} data-testid="landing-page-form-container">
      {showSuccessMessage || updatedFirstPartyData ? (
        <div className="align-center flex min-h-[300px] flex-col justify-center px-16 py-8">
          <p className="m-0 font-suisse-intl text-body-lg font-medium text-grayscale-gray">
            {confirmationText}
          </p>
        </div>
      ) : hydrated && !showSuccessMessage && !updatedFirstPartyData ? (
        <ReCAPTCHA sitekey={RECAPTCHA_SITE_KEY} size="invisible" ref={reCaptchaRef}>
          <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={() => schema(formFields)}
            validateOnMount
            enableReinitialize
          >
            {(formik) => (
              <>
                {formHeadline && (
                  <h2 className="m-0 mb-16 text-center font-suisse-neue text-subheadline-sm">
                    {formHeadline}
                  </h2>
                )}
                {advertisementSponsorLogoUrl && (
                  <span className="mb-16 flex items-center justify-center font-suisse-intl-mono text-caption-sm text-grayscale-gray">
                    Supported by{' '}
                    <a href={advertisementClickthroughUrl} target="_blank" rel="noreferrer">
                      <img
                        onClickCapture={() =>
                          trackEvent(
                            click({
                              clickTarget: ClickTarget.AdvertisementSponsorLogo,
                              clickLink: advertisementClickthroughUrl,
                            })
                          )
                        }
                        data-testid="advertisement-sponsor-logo"
                        alt={`${advertisementSponsorName} logo`}
                        className="ml-16 max-h-40 w-auto"
                      />
                    </a>
                  </span>
                )}
                <TextField className="mb-24" label="Email*" name="email" autoFocus />
                <NUXForm
                  dataFields={formFields}
                  optionalFirstPartyFields={OPTIONAL_FIRST_PARTY_DATA}
                  silenceToast
                />
                <CheckBoxField
                  className="mt-20"
                  name="formVerification"
                  label={AuthoredContentReactToHtmlParser(formVerificationText)}
                />
                <input name="leadType" value={leadType} hidden readOnly />
                <input name="newsletterId" value={newsletterId} hidden readOnly />
                <PrimaryButton
                  as="button"
                  className="mt-20"
                  disabled={!formik.isValid}
                  isLoading={formik.isSubmitting || formik.isValidating}
                  onClick={() => formik.handleSubmit()}
                  onClickCapture={() =>
                    trackEvent(click({ clickTarget: ClickTarget.SubmitButton }))
                  }
                  type="submit"
                  fullWidth
                >
                  {formButtonText}
                </PrimaryButton>
              </>
            )}
          </Formik>
        </ReCAPTCHA>
      ) : (
        <div className="h-[100vh]">
          <CustomSpinner color={CustomSpinnerColor.PrimaryMagenta} style={CUSTOM_SPINNER_STYLES} />
        </div>
      )}
    </div>
  );
};

export default memo(Form);
